/**
 * De zijbalk, met de indeling van de Gate Review-applicatie en de huisstijl van De
 * Klerk Binnenbouw: het blauw van dkb.nl met de gradient naar navy en de bogen uit de
 * kop van de website, het witte logo linksboven en het actieve item als pil.
 *
 * Van boven naar beneden: logo, bedrijf en appnaam, het menu in vaste volgorde,
 * onderaan Ondersteuning, Notificaties en Instellingen, en als laatste de aangemelde
 * gebruiker met de uitlogknop. Het bouwnummer kies je niet hier maar bovenin de
 * schermen die per bouwnummer werken (`Bouwnummerfilter`).
 *
 * Ingeklapt blijft een strook van 80 px met alleen het logo en de iconen; de naam van
 * elk item staat dan als tooltip en voor schermlezers. De stand wordt op dit apparaat
 * onthouden, zodat de werkvloer niet elke keer opnieuw ingeklapt hoeft te worden.
 *
 * Het menu toont alleen wat de rol mag: een tekenaar ziet geen werkvloer. Dat is
 * weergave; de API weigert diezelfde schermen ook.
 */
import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';

import type { Recht } from '../api';
import logo from '../assets/dkb-logo-wit.svg';
import { initialen, useGegevens } from '../gegevens';
import {
  IconAgenda,
  IconBel,
  IconBlokken,
  IconGrafiek,
  IconPaneel,
  IconRaster,
  IconReddingsboei,
  IconTandwiel,
  IconUitloggen,
  IconWerkvloer,
} from './Iconen';

const BEWAARD_INGEKLAPT = 'houtstaat.zijbalkIngeklapt';

export const zijbalkIngeklapt = (): boolean => {
  try {
    return localStorage.getItem(BEWAARD_INGEKLAPT) === '1';
  } catch {
    return false;
  }
};
export const bewaarZijbalk = (ingeklapt: boolean): void => {
  try {
    localStorage.setItem(BEWAARD_INGEKLAPT, ingeklapt ? '1' : '0');
  } catch {
    // Zonder opslag onthoudt de browser het niet; inklappen werkt wel.
  }
};

export const BEDRIJF = 'De Klerk Binnenbouw B.V.';
export const APPNAAM = 'Houtstaat';

const ONDERSTEUNING = `mailto:servicedesk@kdvs.nl?subject=${encodeURIComponent('Houtstaat De Klerk Binnenbouw')}`;

interface MenuItem {
  pad: string;
  label: string;
  recht: Recht;
  icoon: (p: { maat?: number; className?: string }) => ReactNode;
  teller?: number;
}

interface Props {
  ingeklapt?: boolean;
  /** Alleen op de vaste zijbalk; in de schuiflade op een smal scherm niet. */
  zetIngeklapt?: (ingeklapt: boolean) => void;
  /** Sluit de schuiflade na het kiezen van een item. */
  onNavigeer?: () => void;
  notificaties: { open: boolean; ongelezen: number; onKlik: (knop: HTMLButtonElement) => void };
}

export function Zijbalk({ ingeklapt = false, zetIngeklapt, onNavigeer, notificaties }: Props) {
  const { navigatie, mag } = useGegevens();
  const tellingen = navigatie?.tellingen;

  const alles: MenuItem[] = [
    { pad: '/dashboard', label: 'Dashboard', recht: 'dashboardBekijken', icoon: IconGrafiek },
    {
      pad: '/houtstaat',
      label: 'Houtstaat',
      recht: 'houtstaatBekijken',
      icoon: IconRaster,
      teller: tellingen?.bouwelementen,
    },
    {
      pad: '/werkvloer',
      label: 'Werkvloer',
      recht: 'werkvloerBekijken',
      icoon: IconWerkvloer,
      teller: tellingen?.karrenOpenOpWerkvloer,
    },
    {
      pad: '/planning',
      label: 'Planning',
      recht: 'planningBekijken',
      icoon: IconAgenda,
      teller: tellingen?.klaarVoorPlanning,
    },
    { pad: '/bewerkingen', label: 'Bewerkingen', recht: 'regelsBekijken', icoon: IconBlokken },
  ];
  const menu = alles.filter((item) => mag(item.recht));

  const klasse = (actief: boolean, voet = false) =>
    `hs-menu__item${voet ? ' hs-menu__item--voet' : ''}${actief ? ' actief' : ''}`;

  return (
    <div className={`hs-zijbalk${ingeklapt ? ' hs-zijbalk--ingeklapt' : ''}`}>
      <Bogen />

      <div className="hs-zijbalk__kop">
        <img className="hs-zijbalk__logo" src={logo} alt="De Klerk Binnenbouw" />
        {ingeklapt ? null : (
          <>
            <p className="hs-annotatie hs-zijbalk__bedrijf">{BEDRIJF}</p>
            <p className="hs-zijbalk__naam">{APPNAAM}</p>
          </>
        )}
        {zetIngeklapt ? (
          <button
            type="button"
            className="hs-zijbalk__inklap"
            onClick={() => zetIngeklapt(!ingeklapt)}
            aria-expanded={!ingeklapt}
            aria-label={ingeklapt ? 'Menu uitklappen' : 'Menu inklappen'}
            title={ingeklapt ? 'Menu uitklappen' : 'Menu inklappen'}
          >
            <IconPaneel maat={20} open={!ingeklapt} />
          </button>
        ) : null}
      </div>

      <nav aria-label="Hoofdmenu" className="hs-zijbalk__menu">
        <ul className="hs-menu">
          {menu.map(({ pad, label, icoon: Icoon, teller }) => (
            <li key={pad}>
              <NavLink
                to={pad}
                onClick={onNavigeer}
                title={ingeklapt ? label : undefined}
                className={({ isActive }) => klasse(isActive)}
              >
                <Icoon maat={20} className="hs-menu__icoon" />
                <span className="hs-menu__label">{label}</span>
                {teller ? <Teller waarde={teller} ingeklapt={ingeklapt} /> : null}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <ul className="hs-menu hs-menu--voet">
        <li>
          <a href={ONDERSTEUNING} onClick={onNavigeer} title={ingeklapt ? 'Ondersteuning' : undefined} className={klasse(false, true)}>
            <IconReddingsboei maat={20} className="hs-menu__icoon" />
            <span className="hs-menu__label">Ondersteuning</span>
          </a>
        </li>
        <li>
          <button
            type="button"
            aria-haspopup="dialog"
            aria-expanded={notificaties.open}
            onClick={(e) => notificaties.onKlik(e.currentTarget)}
            title={ingeklapt ? 'Notificaties' : undefined}
            className={klasse(notificaties.open, true)}
          >
            <IconBel maat={20} className="hs-menu__icoon" />
            <span className="hs-menu__label">Notificaties</span>
            {notificaties.ongelezen > 0 ? (
              <Teller waarde={notificaties.ongelezen > 9 ? '9+' : notificaties.ongelezen} ingeklapt={ingeklapt} />
            ) : null}
          </button>
        </li>
        <li>
          <NavLink
            to="/instellingen"
            onClick={onNavigeer}
            title={ingeklapt ? 'Instellingen' : undefined}
            className={({ isActive }) => klasse(isActive, true)}
          >
            <IconTandwiel maat={20} className="hs-menu__icoon" />
            <span className="hs-menu__label">Instellingen</span>
          </NavLink>
        </li>
      </ul>

      <Gebruiker ingeklapt={ingeklapt} />
    </div>
  );
}

/** Het getal rechts in een item; ingeklapt is daar geen plaats voor en staat er een stip. */
function Teller({ waarde, ingeklapt }: { waarde: number | string; ingeklapt: boolean }) {
  return ingeklapt ? (
    <span className="hs-menu__stip" aria-label={`${waarde}`} />
  ) : (
    <span className="hs-annotatie hs-menu__teller">{waarde}</span>
  );
}

/**
 * De bogen uit de kop van dkb.nl: concentrische cirkels die naar rechtsonder donkerder
 * worden. Puur decoratief.
 */
function Bogen() {
  return (
    <svg className="hs-zijbalk__bogen" viewBox="0 0 400 400" aria-hidden>
      {[380, 290, 200].map((r) => (
        <circle key={r} cx="400" cy="400" r={r} fill="#000c53" fillOpacity="0.28" />
      ))}
    </svg>
  );
}

/**
 * De aangemelde gebruiker: foto of initialen, naam en rol, en uitloggen.
 *
 * Naam en foto komen uit de aanmelding (zie `profiel.ts`); de API kent alleen het UPN.
 */
function Gebruiker({ ingeklapt }: { ingeklapt: boolean }) {
  const { ik, profiel } = useGegevens();
  const naam = profiel?.weergavenaam ?? ik?.naam ?? '…';
  const rol = ik?.rolLabel.join(', ') || 'Geen rol';
  return (
    <div className="hs-zijbalk__gebruiker">
      <span title={ingeklapt ? `${naam}, ${rol}` : undefined}>
        {profiel?.fotoUrl ? (
          <img className="hs-avatar hs-avatar--foto" src={profiel.fotoUrl} alt="" />
        ) : (
          <span className="hs-avatar">{initialen(naam)}</span>
        )}
      </span>
      <div className="hs-zijbalk__wie">
        <p className="naam" title={profiel?.email ?? ik?.naam}>
          {naam}
        </p>
        <p className="rol">{rol}</p>
      </div>
      <a
        href="/.auth/logout?post_logout_redirect_uri=/"
        className="hs-zijbalk__uitloggen"
        aria-label="Uitloggen"
        title="Uitloggen"
      >
        <IconUitloggen maat={20} />
      </a>
    </div>
  );
}
